import {
  useState,
  useRef,
  useEffect,
  type ComponentType,
  type FormEvent,
  type SVGProps,
} from 'react';
import { api } from '@/api/api-client';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ReactMarkdown from 'react-markdown';
import {
  Bot,
  Send,
  Sparkles,
  Users,
  Mail,
  TrendingUp,
  Target,
  Loader2,
  X,
  Maximize2,
  Minimize2,
} from 'lucide-react';

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

interface QuickPrompt {
  icon: IconType;
  label: string;
  prompt: string;
}

function getQuickPrompts(t: (key: string) => string): QuickPrompt[] {
  return [
    // i18n-exempt-start: prompts sent to the AI assistant; their labels are translated
    { icon: Users, label: t('admin:backoffice.prospectingAssistant.prompts.generateLeads.label'), prompt: 'Génère-moi une liste de 10 leads qualifiés pour mon secteur' },
    { icon: Mail, label: t('admin:backoffice.prospectingAssistant.prompts.outreachMessage.label'), prompt: 'Rédige un email de prospection personnalisé pour un directeur commercial' },
    { icon: TrendingUp, label: t('admin:backoffice.prospectingAssistant.prompts.analyzeResults.label'), prompt: 'Analyse mes derniers appels et suggère des améliorations' },
    { icon: Target, label: t('admin:backoffice.prospectingAssistant.prompts.marketTrends.label'), prompt: 'Quelles sont les tendances actuelles dans mon secteur ?' },
    // i18n-exempt-end
  ];
}

interface ClientLite {
  company_name?: string | null;
}

interface LlmResult {
  answer?: string;
  response?: string;
}

interface ApiShape {
  integrations: {
    Core: {
      InvokeLLM: (args: { feature: 'prospecting_assistant'; prompt: string }) => Promise<string | LlmResult>;
    };
  };
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ProspectingAssistantProps {
  client?: ClientLite | null;
}

export default function ProspectingAssistant({ client }: ProspectingAssistantProps) {
  const { t } = useTranslation(['admin']);
  const QUICK_PROMPTS = getQuickPrompts(t);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (content: string) => {
    if (!content.trim() || isLoading) return;

    setInput('');
    const userMsg: ChatMessage = { role: 'user', content };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const prompt = `Tu es un assistant de prospection commerciale pour ${client?.company_name || 'notre entreprise'}. Réponds en français de manière concise et actionnable.\n\nQuestion: ${content}`;
      const result = await (api as unknown as ApiShape).integrations.Core.InvokeLLM({ feature: 'prospecting_assistant', prompt });
      const answer =
        typeof result === 'string'
          ? result
          : result?.answer || result?.response || JSON.stringify(result);
      setMessages((prev) => [...prev, { role: 'assistant', content: answer }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t('admin:backoffice.prospectingAssistant.genericError') },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (!isOpen) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.05 }}
        onClick={() => setIsOpen(true)}
        type="button"
        aria-label={t('admin:backoffice.prospectingAssistant.open')}
        title={t('admin:backoffice.prospectingAssistant.open')}
        data-testid="assistant-fab"
        // QA-W5-7: smaller below sm, above the iOS home bar (safe area); the
        // portal's content keeps a bottom margin so nothing ends under it.
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 h-12 w-12 sm:bottom-6 sm:right-6 sm:h-14 sm:w-14 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-lg flex items-center justify-center z-50"
      >
        <Sparkles className="w-6 h-6" aria-hidden />
      </motion.button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className={`fixed z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden ${
        isExpanded ? 'inset-4 sm:inset-8' : 'inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] h-[560px] max-h-[80vh] sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[380px]'
      }`}
    >
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">{t('admin:backoffice.prospectingAssistant.title')}</h3>
            <p className="text-white/70 text-xs">{t('admin:backoffice.prospectingAssistant.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? t('admin:backoffice.prospectingAssistant.collapse') : t('admin:backoffice.prospectingAssistant.expand')}
            className="flex h-11 w-11 items-center justify-center text-white/70 hover:text-white transition-colors"
          >
            {isExpanded ? <Minimize2 className="w-4 h-4" aria-hidden /> : <Maximize2 className="w-4 h-4" aria-hidden />}
          </button>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label={t('admin:backoffice.prospectingAssistant.close')}
            className="flex h-11 w-11 items-center justify-center text-white/70 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" aria-hidden />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-purple-100 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-purple-600" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">{t('admin:backoffice.prospectingAssistant.emptyTitle')}</h4>
            <p className="text-sm text-gray-500 mb-6">
              {t('admin:backoffice.prospectingAssistant.emptyHint')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_PROMPTS.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(item.prompt)}
                  className="flex items-center gap-2 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                >
                  <item.icon className="w-4 h-4 text-purple-600 flex-shrink-0" />
                  <span className="text-xs text-gray-700">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <AnimatePresence>
            {messages.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                    msg.role === 'user' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <p className="text-sm">{msg.content}</p>
                  ) : (
                    <div className="text-sm prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl px-4 py-3">
              <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('admin:backoffice.prospectingAssistant.inputPlaceholder')}
            className="flex-1"
            disabled={isLoading}
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || isLoading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </form>
    </motion.div>
  );
}
